import React, { useCallback, useMemo } from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';
import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { InsuranceTermsModal } from '../components/modals/insurance-terms-modal';
import { InsuranceProviders } from '../enums/insurance-providers';
import { useModal } from '../hooks/use-modal';
import Global from '../store/global-selectors';
import { saveUserSetting } from '../store/user/user.actions';

const messages = defineMessages({
  notificationTitle: {
    description: 'decline/ignore terms notification title',
    defaultMessage: 'Cannot create label',
    id: 'l8dVDr',
  },
  shipsuranceBody: {
    description: 'decline/ignore terms notification body',
    defaultMessage:
      'You need to accept the Shipsurance Terms and Conditions to use Shipsurance. Select Create Label to try again or select another insurance option.',
    id: '6MFsIR',
  },
  xcoverBody: {
    description: 'decline/ignore terms notification body for the product "Total Shipping Protection"',
    defaultMessage:
      'You need to accept the Total Shipping Protection Terms and Conditions to use this insurance. Select Create Label to try again or select another insurance option.',
    id: 'SgYexZ',
  },
});

const messageMap = {
  [InsuranceProviders.Shipsurance.insuranceProviderId]: 'shipsuranceBody',
  [InsuranceProviders.XCover.insuranceProviderId]: 'xcoverBody',
};

const acceptedTermsUserSettingNameMap = {
  [InsuranceProviders.Shipsurance.insuranceProviderId]: 'v3.hasAcceptedShipsuranceTerms',
};

const getTermsAcceptedUserSetting = (insuranceProviderId) => ({
  settingName: acceptedTermsUserSettingNameMap[insuranceProviderId],
  settingValue: true,
  excludeV3prefix: true,
});

export const markTermAccepted = (dispatch, insuranceProviderId) => {
  if (acceptedTermsUserSettingNameMap[insuranceProviderId]) {
    dispatch(saveUserSetting(getTermsAcceptedUserSetting(insuranceProviderId)));
  }
};

const checkTerms = (insuranceProviderId, userSettings) => {
  if (acceptedTermsUserSettingNameMap[insuranceProviderId]) {
    const hasAcceptedInsuranceTermsSetting = userSettings.find(
      (setting) => setting?.settingName === acceptedTermsUserSettingNameMap[insuranceProviderId],
    );
    if (hasAcceptedInsuranceTermsSetting?.settingValue) {
      return hasAcceptedInsuranceTermsSetting?.settingValue === true || hasAcceptedInsuranceTermsSetting?.settingValue === 'true';
    }
    return false;
  }
  return true;
};

export const hasUserAcceptedInsuranceTerms = (state, insuranceProviderId) => {
  const userSettings = Global.selectUserUserSettings(state);
  return checkTerms(insuranceProviderId, userSettings);
};

export const useHasUserAcceptedInsuranceTerms = (insuranceProviderId) => {
  const userSettings = useSelector(Global.selectUserUserSettings);
  return useMemo(() => {
    return checkTerms(insuranceProviderId, userSettings);
  }, [insuranceProviderId, userSettings]);
};

const getOnInsuranceTermsModalClose =
  (insuranceProviderId, onDeclinedTerms = noop) =>
  (cancel = true) => {
    if (cancel) {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.notificationTitle} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: <FormattedMessage {...messages[messageMap[insuranceProviderId]]} />,
      });
      onDeclinedTerms();
    }
  };

export const useInsuranceTermsModal = (insuranceProviderId, onAccept, onDecline) => {
  const { open, isOpen, close } = useModal();

  const onClose = useCallback(
    (cancel) => {
      const onInsuranceTermsModalClose = getOnInsuranceTermsModalClose(insuranceProviderId, onDecline);
      onInsuranceTermsModalClose(cancel);
      close();
    },
    [close, insuranceProviderId, onDecline],
  );

  const modalFrameworkMethods = useMemo(() => {
    return {
      onClose,
    };
  }, [onClose]);

  return useMemo(() => {
    return {
      isInsuranceTermsModalOpen: isOpen,
      openInsuranceTermsModal: open,
      onAcceptTerms: onAccept,
      onDeclineTerms: onClose,
      insuranceTermsModalModalFrameworkMethods: modalFrameworkMethods,
    };
  }, [isOpen, modalFrameworkMethods, onAccept, onClose, open]);
};

class InsuranceTermsServiceClass {
  launchInsuranceTermsModal(onAcceptTerms, onDeclinedTerms, insuranceProviderId) {
    ModalService.launchModal({
      component: InsuranceTermsModal,
      props: {
        insuranceProviderId,
        onAcceptTerms,
      },
      onClose: getOnInsuranceTermsModalClose(insuranceProviderId, onDeclinedTerms),
    });
  }
}

export const InsuranceTermsService = new InsuranceTermsServiceClass();
